import React, { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { CheckCircle, Loader2, XCircle, ArrowLeft, RefreshCw } from 'lucide-react';

import { GET_MY_STORE } from '../graphql/queries';
import type { MyStore } from '../hooks/useMyStore';

type Status = 'loading' | 'active' | 'inactive' | 'no-store' | 'error';

interface Props {
  onContinue: () => void;
  onRetry: () => void;
}

/**
 * Landing page after the user returns from payment.rietail.com. Polls the
 * store record to see whether the subscription handler has flipped
 * `isSubscriptionActive` yet — the upstream call back is async, so we
 * give it a few seconds before showing the inactive UI.
 *
 * Mirrors `to-automation/admin-angular/main-app/src/app/account/
 * subscription-verification/subscription-verification.component.ts`.
 */
export const SubscriptionVerification: React.FC<Props> = ({ onContinue, onRetry }) => {
  const client = useApolloClient();
  const [status, setStatus] = useState<Status>('loading');
  const [refreshing, setRefreshing] = useState(false);

  const verify = async (): Promise<Status> => {
    try {
      const res = await client.query<{ getMyStore: MyStore | null }>({
        query: GET_MY_STORE,
        fetchPolicy: 'network-only',
      });
      const store = res.data?.getMyStore;
      if (!store) return 'no-store';
      return store.isSubscriptionActive ? 'active' : 'inactive';
    } catch (err) {
      console.error('[SubscriptionVerification] verify failed', err);
      return 'error';
    }
  };

  // First-load: small retry loop because the upstream's webhook back to us
  // is async (and may take a couple seconds after the user returns).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 6 && !cancelled; i++) {
        const next = await verify();
        if (cancelled) return;
        setStatus(next);
        if (next === 'active') {
          setTimeout(() => !cancelled && onContinue(), 1200);
          return;
        }
        if (next === 'no-store' || next === 'error') return;
        // inactive — wait a beat then re-check
        await new Promise((r) => setTimeout(r, 2000));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    const next = await verify();
    setStatus(next);
    setRefreshing(false);
    if (next === 'active') {
      setTimeout(onContinue, 1200);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="card max-w-md w-full p-8 text-center">
        {status === 'loading' && <LoadingState />}
        {status === 'active' && <ActiveState />}
        {status === 'inactive' && (
          <InactiveState
            refreshing={refreshing}
            onRefresh={handleRefresh}
            onRetry={onRetry}
          />
        )}
        {(status === 'no-store' || status === 'error') && (
          <ErrorState
            message={
              status === 'no-store'
                ? 'Store not found. Please contact support.'
                : 'Could not verify subscription. Try again in a moment.'
            }
            onRetry={handleRefresh}
            refreshing={refreshing}
          />
        )}
      </div>
    </div>
  );
};

// ── Sub-states ────────────────────────────────────────────────

const LoadingState: React.FC = () => (
  <>
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
      <Loader2 className="text-primary animate-spin" size={28} />
    </div>
    <h2 className="text-lg font-black text-text mb-2">Verifying your subscription</h2>
    <p className="text-sm text-text2">
      Hold tight — we're confirming your payment with the subscription service.
    </p>
  </>
);

const ActiveState: React.FC = () => (
  <>
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
      <CheckCircle className="text-emerald-600" size={28} strokeWidth={2.4} />
    </div>
    <h2 className="text-lg font-black text-text mb-2">Subscription active</h2>
    <p className="text-sm text-text2">
      You're all set. Redirecting you back to the dashboard…
    </p>
  </>
);

interface InactiveStateProps {
  refreshing: boolean;
  onRefresh: () => void;
  onRetry: () => void;
}

const InactiveState: React.FC<InactiveStateProps> = ({ refreshing, onRefresh, onRetry }) => (
  <>
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
      <XCircle className="text-amber-600" size={28} strokeWidth={2.4} />
    </div>
    <h2 className="text-lg font-black text-text mb-2">Subscription not active yet</h2>
    <p className="text-sm text-text2 mb-6">
      The subscription service hasn't confirmed your payment yet. This can take a few seconds.
      Try refreshing — or restart the subscription if you cancelled.
    </p>
    <div className="flex gap-2 justify-center">
      <button
        onClick={onRefresh}
        disabled={refreshing}
        className="btn-primary h-10 px-4 inline-flex items-center gap-2"
      >
        <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
        Refresh
      </button>
      <button
        onClick={onRetry}
        className="h-10 px-4 rounded-xl border border-border text-text2 font-bold inline-flex items-center gap-2 hover:bg-gray-50"
      >
        <ArrowLeft size={14} />
        Restart subscription
      </button>
    </div>
  </>
);

interface ErrorStateProps {
  message: string;
  refreshing: boolean;
  onRetry: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ message, refreshing, onRetry }) => (
  <>
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
      <XCircle className="text-red-600" size={28} strokeWidth={2.4} />
    </div>
    <h2 className="text-lg font-black text-text mb-2">Something went wrong</h2>
    <p className="text-sm text-text2 mb-6">{message}</p>
    <button
      onClick={onRetry}
      disabled={refreshing}
      className="btn-primary h-10 px-4 inline-flex items-center gap-2"
    >
      <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
      Try again
    </button>
  </>
);
